import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileWaveform } from "react-icons/fa6";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  ComboboxItem,
  Divider,
  Group,
  Loader,
  Menu,
  NumberInput,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import { Trigger as TabsTrigger } from "@radix-ui/react-tabs";
import {
  IconArrowBackUp,
  IconDownload,
  IconInvoice,
  IconReceipt2,
} from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { ConsultationSelect } from "@components/ConsultationSelect";
import { FilePreviewModal } from "@components/FilePreviewModal";
import { TabsContent, TabsRoot } from "@components/Tabs";
import { StyledTabsList } from "@components/Tabs/Tabs.styled";

import { formatDateToString } from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { discounts } from "@data/discounts";
import { getBillStatuses, getPaymentMethods } from "@data/dropdownValues";

import { MAX_PAGE_SIZE, PAGE_TITLE } from "@constants/index";

import { useAddDiscount } from "@hooks/mutations/billing/useAddDiscount";
import { useCreateTransaction } from "@hooks/mutations/billing/useCreateTransaction";
import { useRemoveDiscount } from "@hooks/mutations/billing/useRemoveDiscount";
import { useGetBillForConsultation } from "@hooks/queries/billing/useGetBillForConsultation";
import { useGetInvoice } from "@hooks/queries/billing/useGetInvoice";
import { useGetConsultationsForPatient } from "@hooks/queries/consultation/useGetConsultationsForPatient";

import { useAuth } from "@contexts/auth";

import { StatusSelect } from "./components/StatusSelect";
import { TransactionsTable } from "./components/TransactionsTable";
import { StyledBilling } from "./Billing.styled";


const schema = () =>
  z.object({
    paymentMethod: z.string().min(1, i18n.t("payment-method-required")),
    amountPaid: z.number().min(1, i18n.t("amount-paid-required")),
  });

type Inputs = z.infer<ReturnType<typeof schema>>;

export const Billing = () => {
  const { t, i18n } = useTranslation();

  useDocumentTitle(`${t("billing")} • ${PAGE_TITLE}`);
  const { patientId, consultationId: consultationIdFromParam } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [currentTab, setCurrentTab] = useState("services");
  const [consultationId, setConsultationId] = useState<string | null>(
    consultationIdFromParam || null
  );
  const [isReadyToGetInvoice, setIsReadyToGetInvoice] = useState(false);
  const [
    openedReceiptFilePreviewModal,
    { open: openReceiptFilePreviewModal, close: closeReceiptFilePreviewModal },
  ] = useDisclosure(false);
  const [
    openedInvoiceFilePreviewModal,
    { open: openInvoiceFilePreviewModal, close: closeInvoiceFilePreviewModal },
  ] = useDisclosure(false);

  const [serviceStatuses, setServiceStatuses] = useState<
    Record<string, string>
  >({});

  const { data: consultationsData } = useGetConsultationsForPatient({
    patientId: patientId as string,
    pagination: { page: 1, pageSize: MAX_PAGE_SIZE },
  });

  const consultationsComboboxItems = useMemo(() => {
    return consultationsData?.newData?.map(item => ({
      label: item.cId,
      value: item.id,
      date: `${formatDateToString(item.date)}, ${item.date.toLocaleTimeString(
        i18n.language,
        { hour: "2-digit", minute: "2-digit" }
      )}`,
    }));
  }, [consultationsData]);

  const {
    data: billData,
    isLoading: isBillDataLoading,
    isError: isBillDataError,
    isSuccess: isBillDataSuccess,
    refetch: refetchBill,
    fetchStatus: billDataFetchStatus,
  } = useGetBillForConsultation({
    consultationId: state?.consultationId ?? (consultationId || ""),
    isEnabled: false,
  });
  const { mutate: createTransaction, isLoading: isCreateTransactionLoading } =
    useCreateTransaction();
  const {
    data: billFiles,
    isLoading: isInvoiceLoading,
    fetchStatus: invoiceFetchStatus,
  } = useGetInvoice({
    billId: billData?.id as string,
    isEnabled: isReadyToGetInvoice && !!billData?.id,
  });
  const { mutate: addDiscount, isLoading: isAddDiscountLoading } =
    useAddDiscount();
  const { mutate: removeDiscount, isLoading: isRemoveDiscountLoading } =
    useRemoveDiscount();

  const [discount, setDiscount] = useState<ComboboxItem | null>(null);

  const form = useForm({
    mode: "controlled",
    validate: zodResolver(schema()),
    initialValues: {
      paymentMethod: "",
      amountPaid: 0,
    },
  });

  const subtotalAfterDiscount = useMemo(() => {
    // prettier-ignore
    return +(billData?.subtotal ?? 0) - ((billData?.subtotal ?? 0) * (+(discount?.value ?? 0)/100));
  }, [billData?.subtotal, discount?.value]);

  const hasAdminDiscount = billData?.deductions?.some(
    item => item.discountType === "admin"
  );

  const handleOnSubmit = (values: Inputs) => {
    if (
      (billData?.balance ?? 0) > 0 &&
      values.amountPaid > (billData?.balance ?? 0)
    ) {
      form.setFieldError("amountPaid", t("error-amount-exceeds-balance"));
      return;
    }

    createTransaction(
      {
        billId: billData?.id as string,
        transaction: {
          paymentMethod: values.paymentMethod,
          amountPaid: values.amountPaid,
        },
      },
      {
        onSuccess: () => {
          setCurrentTab("transactions");
          form.reset();
          refetchBill();
        },
      }
    );
  };

  useEffect(() => {
    if (state?.consultationId) {
      refetchBill();
    } else {
      // Explicitly state else block, so that the query refetches when it is made null because it hangs for some reason
      if (consultationId) {
        refetchBill();
      }
    }
  }, [state?.consultationId]);

  useEffect(() => {
    if (consultationId && consultationsData) {
      if (state?.consultationId) {
        state.consultationId = null;
      }
      refetchBill();
    }
  }, [consultationId]);

  // useEffect(() => {
  //   if (Object.keys(formData).length && billData?.totalAccepted) {
  //     if (
  //       (formData?.amountPaid ?? 0) > billData.totalAccepted ||
  //       (formData?.amountPaid ?? 0) > subtotalAfterDiscount
  //     ) {
  //       setAmountPaidError(true);
  //     } else setAmountPaidError(false);
  //   }
  // }, [formData]);

  useEffect(() => {
    if (billData) {
      const initial: Record<string, string> = {};
      billData?.services?.forEach(service => {
        if (service.id) {
          initial[service.id] = service.status ?? "";
        }
      });

      setServiceStatuses(initial);
      if (hasAdminDiscount) {
        const discountFromBackend =
          billData?.deductions
            ?.find(item => item.discountType === "admin")
            ?.discountPercentage?.toString() ?? "";

        if (discountFromBackend) {
          setDiscount(
            discounts.find(
              item => item.value === discountFromBackend
            ) as ComboboxItem
          );
        }
      }
    }
  }, [billData]);

  return (
    <WithSidebar hasPatientNavbar hasBackground={false}>
      <WithPatientNavbar>
        <StyledBilling>
          <div className="content">
            <div className="summary-section">
              <div className="header">
                <Group justify="space-between" w="100%">
                  <h2>{t("bill")}</h2>
                  <Menu
                    transitionProps={{ transition: "pop" }}
                    withArrow
                    position="bottom-end"
                    withinPortal
                    onClose={() => setIsReadyToGetInvoice(false)}>
                    <FilePreviewModal
                      opened={openedReceiptFilePreviewModal}
                      close={closeReceiptFilePreviewModal}
                      docs={
                        billData?.files.length
                          ? billData?.files
                              ?.filter(item => {
                                return item.includes("receipt");
                              })
                              .map(item => ({
                                uri: "https://" + item + `?token=${token}`,
                                fileName: (item as string).split("/files/")[1],
                              }))
                          : []
                      }
                    />
                    <FilePreviewModal
                      opened={openedInvoiceFilePreviewModal}
                      close={closeInvoiceFilePreviewModal}
                      docs={
                        billFiles?.length
                          ? billFiles
                              ?.filter(item => {
                                return item.includes("invoice");
                              })
                              .map(item => ({
                                uri: "https://" + item + `?token=${token}`,
                                fileName: (item as string).split("/files/")[1],
                              }))
                          : []
                      }
                    />
                    <Menu.Target>
                      <Button
                        leftSection={<IconDownload size={16} />}
                        disabled={!billData}
                        loading={
                          isInvoiceLoading && invoiceFetchStatus === "fetching"
                        }>
                        {t("download")}...
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <PermissionGate permission="billing.export"><Menu.Item
                        leftSection={<IconInvoice size={16} />}
                        onClick={() => {
                          openInvoiceFilePreviewModal();
                        }}>
                        {t("download-invoice")}
                      </Menu.Item></PermissionGate>
                      <Menu.Item
                        leftSection={<IconReceipt2 size={16} />}
                        disabled={
                          !billData?.files?.some(fileName =>
                            fileName.includes("receipt")
                          )
                        }
                        onClick={() => {
                          openReceiptFilePreviewModal();
                        }}>
                        {t("download-receipt")}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              </div>

              <Stack gap={"1rem"} mt="1rem">
                <div className="form-field">
                  <ConsultationSelect
                    label={t("consultation")}
                    placeholder={t("select-consultation")}
                    data={consultationsComboboxItems || []}
                    value={
                      state?.consultationId
                        ? state.consultationId
                        : consultationId
                    }
                    setValue={val => {
                      setConsultationId(val);
                      setIsReadyToGetInvoice(true);
                      // Navigate to URL with consultationId
                      if (val && patientId) {
                        navigate(`/patients/${patientId}/billing/${val}`);
                      }
                    }}
                  />
                </div>

                <Group justify="space-between">
                  <Text c="dimmed" fz={14}>
                    {t("date")}:
                  </Text>
                  <Text fz={14}>
                    {billData
                      ? formatDateToString(billData.consultationInfo.date)
                      : "N/A"}
                  </Text>
                </Group>
                <Group justify="space-between" align="start">
                  <Text c="dimmed" fz={14}>
                    {t("support-staff")}:
                  </Text>

                  <Stack gap={"0.5rem"} align="end">
                    {billData?.consultationInfo?.supportStaff?.length ? (
                      billData.consultationInfo.supportStaff.map(
                        (item, index) => (
                          <Badge
                            fz={12}
                            c="var(--blue-11)"
                            bg={"var(--blue-3)"}
                            key={index}>
                            {item.fullName}
                          </Badge>
                        )
                      )
                    ) : (
                      <Text fz={14}>N/A</Text>
                    )}
                  </Stack>
                </Group>
                <Group justify="space-between">
                  <Text fz={14} c="dimmed">
                    {t("time-started")}:
                  </Text>
                  <Text fz={14}>
                    {billData
                      ? billData?.consultationInfo.createdAt.toLocaleTimeString(
                          i18n.language
                        )
                      : "N/A"}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text fz={14} c="dimmed">
                    {t("time-finalized")}:
                  </Text>
                  <Text fz={14}>
                    {billData
                      ? billData?.consultationInfo.updatedAt.toLocaleTimeString(
                          i18n.language
                        )
                      : "N/A"}
                  </Text>
                </Group>
                <Divider />

                <Title order={3} fz={14} fw={600}>
                  {t("cost")}
                </Title>

                <Group justify="space-between">
                  <Text fz={14} c="dimmed">
                    {t("total")}:
                  </Text>
                  <Text fz={14}>
                    {Intl.NumberFormat(i18n.language, {
                      style: "currency",
                      currency: "CFA",
                    }).format(billData?.total || 0)}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text fz={14} c="dimmed">
                    {t("accepted-total")}:
                  </Text>
                  <Text fz={14}>
                    {Intl.NumberFormat(i18n.language, {
                      style: "currency",
                      currency: "CFA",
                    }).format(billData?.totalAccepted || 0)}
                  </Text>
                </Group>

                <Divider />

                <Title order={3} fz={14} fw={600}>
                  {t("discounts")}
                </Title>
                {billData?.deductions.length ? (
                  <>
                    {billData.deductions.map((item, index) =>
                      item.discountType === "promo" ? (
                        <Group justify="space-between" key={index}>
                          <Text fz={14} c="dimmed">
                            {t("promo-discount")}:
                          </Text>
                          <Text fz={14}>
                            {Intl.NumberFormat(i18n.language, {
                              style: "currency",
                              currency: "CFA",
                            }).format(-item.discountAmount || 0)}
                          </Text>
                        </Group>
                      ) : (
                        <Group justify="space-between" key={index}>
                          <Text fz={14} c="dimmed">
                            {t("admin-discount")}:
                          </Text>
                          <Text fz={14}>
                            {Intl.NumberFormat(i18n.language, {
                              style: "currency",
                              currency: "CFA",
                            }).format(-item.discountAmount || 0)}
                          </Text>
                        </Group>
                      )
                    )}
                  </>
                ) : (
                  <Text fz={14} c="dimmed">
                    N/A
                  </Text>
                )}

                <Divider />

                <Group justify="space-between">
                  <Text fz={14} c="dimmed">
                    {t("subtotal")}:
                  </Text>
                  <Text fz={14}>
                    {Intl.NumberFormat(i18n.language, {
                      style: "currency",
                      currency: "CFA",
                    }).format(billData?.subtotal || 0)}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text fz={14} c="dimmed">
                    {t("amount-paid")}:
                  </Text>
                  <Text fz={14}>
                    {Intl.NumberFormat(i18n.language, {
                      style: "currency",
                      currency: "CFA",
                    }).format(billData?.amountPaid || 0)}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text fz={14} c="dimmed" fw={500}>
                    {t("balance")}:
                  </Text>
                  <Text fz={15} fw={500} c="accent">
                    {Intl.NumberFormat(i18n.language, {
                      style: "currency",
                      currency: "CFA",
                    }).format(billData?.balance || 0)}
                  </Text>
                </Group>
              </Stack>
            </div>

            <div className="details-section">
              <TabsRoot
                className="tab-root"
                value={currentTab}
                onValueChange={val => {
                  // if (!consultationId) return;
                  setCurrentTab(val);
                }}>
                <div className="header">
                  <h2>{t("details")}</h2>
                  <StyledTabsList>
                    <TabsTrigger
                      className="tabs-trigger"
                      value={"services"}
                      key={"Services"}>
                      {t("services")}
                    </TabsTrigger>
                    <TabsTrigger
                      className="tabs-trigger"
                      value={"payments"}
                      key={"Payments"}>
                      {t("payments")}
                    </TabsTrigger>
                    <TabsTrigger
                      className="tabs-trigger"
                      value={"transactions"}
                      key={"Transactions"}
                      disabled={!billData?.id}>
                      {t("transactions")}
                    </TabsTrigger>
                  </StyledTabsList>
                </div>

                <TabsContent value="services" className="tab">
                  {renderLoadingErrorData({
                    shouldStartEmpty: true,
                    isLoading:
                      isBillDataLoading &&
                      (consultationId || state?.consultationId),
                    isError: isBillDataError,
                    isSuccess: isBillDataSuccess,
                    dataLength: Object.keys(billData ?? {}).length,
                    errorComponent: (
                      <Center h={"100%"} mih={200}>
                        <Stack justify="center" gap={"xs"}>
                          <Text ta={"center"} fw={500}>
                            {t("oops")}
                          </Text>
                          <Text ta={"center"} c="dimmed" fz={"sm"}>
                            {t("encountered-error")}
                          </Text>
                        </Stack>
                      </Center>
                    ),
                    emptyDataComponent: (
                      <Center h={"100%"} mih={200}>
                        <Stack justify="center" gap={"xs"}>
                          <Text ta={"center"}>
                            {t("no-consultation-selected")}
                          </Text>
                          <Text ta={"center"} c="dimmed" fz={"sm"}>
                            {t("no-consultation-selected-description")}
                          </Text>
                        </Stack>
                      </Center>
                    ),
                    loadingComponent: (
                      <Center h={"100%"} mih={200}>
                        <Stack justify="center" gap={5}>
                          <Loader size={"md"} color="var(--clr-primary-400)" />
                        </Stack>
                      </Center>
                    ),
                    dataComponent: (
                      <ul className="items-list">
                        {billData?.services?.map((service, index) => (
                          <li className="item" key={index}>
                            <div className="wrapper">
                              <div className="icon-wrapper">
                                <FaFileWaveform />
                              </div>
                              <div className="text-content">
                                <p className="title">
                                  {service.type}{" "}
                                  {service?.quantity && service.quantity > 1 ? (
                                    <span>&nbsp;({service.quantity}x)</span>
                                  ) : null}
                                </p>
                                <div className="staff-wrapper">
                                  <ul className="staff-list">
                                    <li className="staff-list-item service-category">
                                      {t(
                                        `${service.category
                                          .split("_")
                                          .join("-")}`
                                      )}
                                    </li>
                                    {/* <li
                              className={`staff-list-item bill-category  ${service.status}`}>
                              {t(`${service.status}`)}
                            </li> */}
                                    <li>
                                      <StatusSelect
                                        refetchBill={refetchBill}
                                        consultationId={
                                          state?.consultationId ??
                                          consultationId
                                        }
                                        billId={billData?.id}
                                        serviceId={service.id}
                                        setValue={(newStatus: string) =>
                                          setServiceStatuses(prev => ({
                                            ...prev,
                                            [service.id]: newStatus,
                                          }))
                                        }
                                        value={
                                          serviceStatuses[service.id] ?? null
                                        }
                                        data={getBillStatuses()}
                                      />
                                    </li>
                                    {service.category === "consultation" ? (
                                      <li className="staff-list-item">
                                        {
                                          billData?.consultationInfo?.physician
                                            .fullName
                                        }
                                      </li>
                                    ) : null}
                                    {service.supportStaff?.id ? (
                                      <li className="staff-list-item support">
                                        {service.supportStaff?.fullName}
                                      </li>
                                    ) : null}
                                  </ul>
                                </div>
                              </div>
                            </div>
                            <div className="amount-section">
                              <p className="amount">
                                {Intl.NumberFormat(i18n.language, {
                                  style: "currency",
                                  currency: "CFA",
                                }).format(+service.price)}
                              </p>
                              <p className="date">
                                {formatDateToString(service.createdAt)}
                                {", "}
                                {service.createdAt.toLocaleTimeString(
                                  i18n.language,
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ),
                  })}
                </TabsContent>

                <TabsContent value="payments" className="tab">
                  <form
                    className="form"
                    onSubmit={form.onSubmit(
                      values => handleOnSubmit(values),
                      errors => {
                        const firstErrorPath = Object.keys(errors)[0];
                        form.getInputNode(firstErrorPath)?.focus();
                      }
                    )}>
                    <div className="form-group">
                      <div className="form-field">
                        <Select
                          label={t("discount")}
                          placeholder={t("select-discount")}
                          data={discounts}
                          value={discount?.value ?? null}
                          onChange={(_, option) => setDiscount(option)}
                          clearable
                          allowDeselect
                          disabled={
                            hasAdminDiscount ||
                            (!consultationId && !state?.consultationId) ||
                            !billData?.balance
                          }
                        />
                      </div>
                      <div className="form-field">
                        <Stack mt="0.5rem" gap={discount ? "0.5rem" : "1rem"}>
                          <Text fz={14} fw={500}>
                            {t("subtotal-after-discount")}
                          </Text>
                          {discount ? (
                            <Group>
                              <Text fz={14} c="accent" fw={500}>
                                {Intl.NumberFormat(i18n.language, {
                                  style: "currency",
                                  currency: "CFA",
                                }).format(
                                  (hasAdminDiscount
                                    ? billData?.subtotal
                                    : subtotalAfterDiscount) ?? 0
                                )}
                              </Text>
                              {hasAdminDiscount ? (
                                <>
                                  <Text fz={13} c="green">
                                    {t("discount-applied")}
                                  </Text>
                                  <Tooltip
                                    label={t("remove-discount")}
                                    withArrow
                                    events={{
                                      focus: true,
                                      hover: true,
                                      touch: true,
                                    }}>
                                    <ActionIcon
                                      variant="outline"
                                      loading={isRemoveDiscountLoading}
                                      onClick={() => {
                                        removeDiscount(
                                          { billId: billData?.id ?? "" },
                                          {
                                            onSuccess: () => {
                                              refetchBill();
                                            },
                                          }
                                        );
                                      }}>
                                      <IconArrowBackUp size={16} stroke={1.5} />
                                    </ActionIcon>
                                  </Tooltip>
                                </>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="xs"
                                  loading={isAddDiscountLoading}
                                  onClick={() => {
                                    addDiscount(
                                      {
                                        billId: billData?.id ?? "",
                                        discount: +(discount?.value ?? 0),
                                      },
                                      {
                                        onSuccess: () => {
                                          refetchBill();
                                        },
                                      }
                                    );
                                  }}>
                                  {t("apply-discount")}
                                </Button>
                              )}
                            </Group>
                          ) : (
                            <Text fz={14} c="dimmed">
                              {t("no-discount-selected")}
                            </Text>
                          )}
                        </Stack>
                      </div>
                    </div>

                    <div className="form-group">
                      <div className="form-field">
                        <Select
                          label={t("payment-method")}
                          withAsterisk
                          placeholder={t("select-payment-method")}
                          data={getPaymentMethods()}
                          {...form.getInputProps("paymentMethod")}
                          key={"paymentMethod"}
                          disabled={
                            (!consultationId && !state?.consultationId) ||
                            !billData?.balance
                          }
                        />
                      </div>
                      <div className={`form-field`}>
                        <NumberInput
                          label={t("amount-paid")}
                          withAsterisk
                          id="amount"
                          min={0}
                          max={billData?.balance}
                          {...form.getInputProps("amountPaid")}
                          key="amountPaid"
                          disabled={
                            (!consultationId && !state?.consultationId) ||
                            !billData?.balance
                          }
                        />
                      </div>
                    </div>

                    <div className="form-footer">
                      <Tooltip
                        withArrow
                        label={t("no-balance")}
                        opened={billData?.balance === 0}
                        events={{ hover: true, focus: true, touch: true }}>
                        <PermissionGate permission="billing.payment"><Button
                          type="submit"
                          disabled={
                            (!consultationId && !state?.consultationId) ||
                            !billData?.balance
                          }
                          loading={isCreateTransactionLoading}>
                          {t("add-payment")}
                        </Button></PermissionGate>
                      </Tooltip>
                    </div>
                  </form>
                </TabsContent>

                <TabsContent value="transactions">
                  <TransactionsTable
                    isError={isBillDataError}
                    isLoading={isBillDataLoading}
                    isSuccess={isBillDataSuccess}
                    data={billData?.transactions || []}
                  />
                </TabsContent>
              </TabsRoot>
            </div>
          </div>
        </StyledBilling>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
